import { OPPORTUNITY_STATUS } from "./constants";

/** Primary lifecycle steps shown on opportunity detail */
export const OPPORTUNITY_LIFECYCLE_STEPS = [
  { key: OPPORTUNITY_STATUS.DRAFT, label: "Draft" },
  { key: OPPORTUNITY_STATUS.PUBLISHED, label: "Published" },
  { key: OPPORTUNITY_STATUS.FUNDING, label: "Funding" },
  { key: OPPORTUNITY_STATUS.ACTIVE, label: "Active" },
  { key: OPPORTUNITY_STATUS.MATURED, label: "Matured" },
  { key: OPPORTUNITY_STATUS.CLOSED, label: "Closed" },
];

const LIFECYCLE_INDEX = Object.fromEntries(
  OPPORTUNITY_LIFECYCLE_STEPS.map((s, i) => [s.key, i])
);

/** Map ancillary statuses onto the visual stepper index */
const STATUS_STEP_ALIASES = {
  [OPPORTUNITY_STATUS.INTERNAL_REVIEW]: OPPORTUNITY_STATUS.DRAFT,
  [OPPORTUNITY_STATUS.SCHEDULED]: OPPORTUNITY_STATUS.DRAFT,
  [OPPORTUNITY_STATUS.FULLY_FUNDED]: OPPORTUNITY_STATUS.FUNDING,
  [OPPORTUNITY_STATUS.ARCHIVED]: OPPORTUNITY_STATUS.CLOSED,
};

export function normalizeOpportunityStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "unpublished") return OPPORTUNITY_STATUS.DRAFT;
  return s;
}

export function getLifecycleStepIndex(status) {
  const normalized = normalizeOpportunityStatus(status);
  const key = STATUS_STEP_ALIASES[normalized] || normalized;
  return LIFECYCLE_INDEX[key] ?? 0;
}

export function getOpportunityLifecycleActions(status) {
  const s = normalizeOpportunityStatus(status);

  const actions = {
    [OPPORTUNITY_STATUS.DRAFT]: [
      { label: "Publish", target: OPPORTUNITY_STATUS.PUBLISHED, variant: "primary" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.INTERNAL_REVIEW]: [
      { label: "Publish now", target: OPPORTUNITY_STATUS.PUBLISHED, variant: "primary" },
      { label: "Return to draft", target: OPPORTUNITY_STATUS.DRAFT, variant: "ghost" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.SCHEDULED]: [
      { label: "Publish now", target: OPPORTUNITY_STATUS.PUBLISHED, variant: "primary" },
      { label: "Move to draft", target: OPPORTUNITY_STATUS.DRAFT, variant: "ghost" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.PUBLISHED]: [
      { label: "Pause / unpublish", target: OPPORTUNITY_STATUS.DRAFT, variant: "ghost" },
      { label: "Start funding", target: OPPORTUNITY_STATUS.FUNDING, variant: "outline" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.FUNDING]: [
      { label: "Mark fully funded", target: OPPORTUNITY_STATUS.FULLY_FUNDED, variant: "teal" },
      { label: "Activate investment", target: OPPORTUNITY_STATUS.ACTIVE, variant: "success" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.FULLY_FUNDED]: [
      {
        label: "Reopen for investment",
        target: OPPORTUNITY_STATUS.FUNDING,
        apiAction: "reopen-funding",
        variant: "primary",
      },
      { label: "Activate investment", target: OPPORTUNITY_STATUS.ACTIVE, variant: "success" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.ACTIVE]: [
      {
        label: "Reopen for investment",
        target: OPPORTUNITY_STATUS.FUNDING,
        apiAction: "reopen-funding",
        variant: "primary",
      },
      { label: "Mark matured", target: OPPORTUNITY_STATUS.MATURED, variant: "teal" },
      { label: "Preview public page", action: "preview", variant: "outline" },
    ],
    [OPPORTUNITY_STATUS.MATURED]: [
      { label: "Close opportunity", target: OPPORTUNITY_STATUS.CLOSED, variant: "outline" },
      { label: "Archive", target: OPPORTUNITY_STATUS.ARCHIVED, variant: "ghost" },
    ],
    [OPPORTUNITY_STATUS.CLOSED]: [
      { label: "Archive", target: OPPORTUNITY_STATUS.ARCHIVED, variant: "ghost" },
    ],
    [OPPORTUNITY_STATUS.ARCHIVED]: [],
  };

  return actions[s] || actions[OPPORTUNITY_STATUS.DRAFT];
}

export function isOpportunityPubliclyVisible(status) {
  const s = normalizeOpportunityStatus(status);
  return [
    OPPORTUNITY_STATUS.PUBLISHED,
    OPPORTUNITY_STATUS.FUNDING,
    OPPORTUNITY_STATUS.FULLY_FUNDED,
    OPPORTUNITY_STATUS.ACTIVE,
    OPPORTUNITY_STATUS.MATURED,
  ].includes(s);
}

/** Matches backend edit gate — hide Edit when false. */
export function isOpportunityEditable(status) {
  const s = normalizeOpportunityStatus(status);
  return [
    OPPORTUNITY_STATUS.DRAFT,
    OPPORTUNITY_STATUS.INTERNAL_REVIEW,
    OPPORTUNITY_STATUS.SCHEDULED,
    OPPORTUNITY_STATUS.PUBLISHED,
    OPPORTUNITY_STATUS.FUNDING,
  ].includes(s);
}
